import { i18n } from 'next-i18next'
import { Schema, date, object, string } from 'yup'
import { Config } from 'modules/config/state/config-types'
import { OrderContactAnswersSlice } from './contact-answers-types'
import { PHONE_VALIDATION_REGEX } from 'utils/constants/misc'
import { Question } from 'models/Question'

type Tag = NonNullable<Question['tag']>

const tagKeyMapping: Partial<Record<Tag, string>> = {
  homephone: 'phone',
}

export const makeOrderContactAnswersValidationSchema = (config: Config) => {
  const t = i18n?.t.bind(i18n) || ((key: string, fallback: string) => fallback)

  const REQUIRED_MESSAGE = t('ContactAnswers.validation.fieldRequired', 'Field required')
  const MAX_LENGTH_MESSAGE = t('ContactAnswers.validation.limitExceeded', 'You are over the limit')

  const schemaFields = config.questions.contact.reduce((acc, cur) => {
    if (!cur.tag || cur.tag === 'homephone') {
      return acc
    }

    const lenTestFn = (value?: string) => (value === undefined ? true : value.length <= 255)

    const requiredTestFn = (value?: string | boolean | number | Date) =>
      cur.is_required ? !!value : true

    if (cur.tag === 'cellphone') {
      acc[cur.tag] = string()
        .test('required', REQUIRED_MESSAGE, requiredTestFn)
        .matches(PHONE_VALIDATION_REGEX, {
          message: t(
            'ContactAnswers.validation.invalidPhone',
            'Please enter a valid phone number.',
          ),
          excludeEmptyString: true,
        })
    } else if (cur.tag === 'birthdate') {
      const today = new Date()

      const aHunderedAndFiftyYearsAgo = new Date()
      aHunderedAndFiftyYearsAgo.setFullYear(today.getFullYear() - 150)

      acc[cur.tag] = date()
        .test('required', REQUIRED_MESSAGE, requiredTestFn)
        .min(
          aHunderedAndFiftyYearsAgo,
          t('ContactAnswers.validation.invalidBirthday', 'Please enter a valid birthday'),
        )
        .max(today, t('ContactAnswers.validation.invalidBirthday', 'Please enter a valid birthday'))
        .transform((curr, orig) => (orig === '' || orig === null ? undefined : curr))
    } else {
      switch (cur.type) {
        case 'string':
        case 'text':
          acc[cur.tag] = string()
            .test('required', REQUIRED_MESSAGE, requiredTestFn)
            .test('len', MAX_LENGTH_MESSAGE, lenTestFn)

          break

        case 'enum':
          acc[cur.tag] = string().test('required', REQUIRED_MESSAGE, requiredTestFn)

          break
        default:
          console.warn(`Unsupported type: ${cur.type}`)

          break
      }
    }

    return acc
  }, {} as Record<string, Schema>)

  return object(schemaFields)
}

export const makeOrderContactAnswersPayload = (config: Config, value: OrderContactAnswersSlice) => {
  return config.questions.contact
    .map((question) => {
      const { tag, id } = question

      // If no tag defined, return empty answer with question ID
      if (!tag) {
        return { question_id: id, answer: '' }
      }

      // Map tag to the correct key in OrderContactAnswersSlice
      const actualKey = (tagKeyMapping[tag] || tag) as keyof OrderContactAnswersSlice
      const answer = value[actualKey] as string | undefined

      return {
        question_id: question.id,
        answer: answer || '',
      }
    })
    .filter((answer) => !!answer.answer)
}
